import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  Divider,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

// Partner card component
interface PartnerCardProps {
  icon: string;
  title: string;
  description: string;
  features: string[];
  website: string;
}

const PartnerCard: React.FC<PartnerCardProps> = ({ icon, title, description, features, website }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        borderTop: '4px solid #015480',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          borderTop: '4px solid #0277bd',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          component="img"
          src={icon}
          alt={`${title} logo`}
          sx={{
            width: 60,
            height: 60,
            objectFit: 'contain',
            mr: 2,
            p: 1,
            borderRadius: 1,
            background: '#f5f5f5'
          }}
        />
        <Typography variant="h5" fontWeight={600} color="primary">
          {title}
        </Typography>
      </Box>
      
      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', flex: 1 }}>
        {description}
      </Typography>
      
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Key Features:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {features.map((feature, idx) => (
            <Chip 
              key={idx} 
              label={feature} 
              size="small" 
              sx={{ 
                background: 'rgba(2, 119, 189, 0.1)', 
                color: 'primary.main',
                fontWeight: 500
              }} 
            />
          ))}
        </Box>
      </Box>
      
      <Button 
        variant="outlined" 
        color="primary" 
        href={website} 
        target="_blank"
        sx={{ alignSelf: 'flex-start', mt: 'auto' }}
      >
        Learn More
      </Button>
    </Paper>
  );
};

const Partners: React.FC = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Helper function to safely get string from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };

  const partners = [
    {
      icon: '/resources/partners/ovh.png',
      title: getTranslationString('partners.ovh.title', 'OVH'),
      description: getTranslationString('partners.ovh.description', 'We leverage OVH for high-performance and reliable server hosting.'),
      features: [
        'Global Network', 
        'Anti-DDoS Protection', 
        'ISO/IEC 27001 Certified', 
        'High Availability', 
        'Scalable Infrastructure'
      ],
      website: 'https://www.ovhcloud.com/'
    },
    {
      icon: '/resources/partners/firebase.png',
      title: getTranslationString('partners.firebase.title', 'Firebase'),
      description: getTranslationString('partners.firebase.description', 'Firebase powers our applications with Google\'s comprehensive development platform.'),
      features: [
        'Real-time Database', 
        'Authentication', 
        'Cloud Functions', 
        'Analytics', 
        'Cloud Messaging'
      ],
      website: 'https://firebase.google.com/'
    },
    {
      icon: '/resources/partners/supabase.png',
      title: getTranslationString('partners.supabase.title', 'Supabase'),
      description: getTranslationString('partners.supabase.description', 'For projects requiring an open-source PostgreSQL-based alternative.'),
      features: [
        'PostgreSQL Database', 
        'Authentication', 
        'Storage', 
        'Edge Functions', 
        'Realtime Subscriptions'
      ],
      website: 'https://supabase.com/'
    },
    {
      icon: '/resources/partners/mongodb.png',
      title: getTranslationString('partners.mongodb.title', 'MongoDB'),
      description: getTranslationString('partners.mongodb.description', 'We implement MongoDB as our flexible NoSQL solution.'),
      features: [
        'Document Database', 
        'Atlas Cloud', 
        'Aggregation Pipeline', 
        'Horizontal Scaling', 
        'Multi-Cloud Deployment'
      ],
      website: 'https://www.mongodb.com/'
    },
    {
      icon: '/resources/partners/cursor-chatgpt.png',
      title: getTranslationString('partners.cursor.title', 'Cursor + ChatGPT'),
      description: getTranslationString('partners.cursor.description', 'Our development workflow is accelerated by Cursor.'),
      features: [
        'AI-Assisted Coding', 
        'Code Explanation', 
        'Code Generation', 
        'Refactoring', 
        'Documentation'
      ],
      website: 'https://cursor.sh/'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Paper
        sx={{
          py: { xs: 6, md: 10 },
          px: 2,
          mb: 6,
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: 'url(/resources/scene3.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(1, 84, 128, 0.8)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              }}
            >
              {getTranslationString('partners.title', 'Our Tech Stack & Partners')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                fontWeight: 400,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.5rem' },
              }}
            >
              {getTranslationString('partners.subtitle', 'The powerful technologies behind our solutions')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                maxWidth: '700px',
                mx: 'auto',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.7,
              }}
            >
              {t('about.intro')}
            </Typography>
          </Box>
        </Container>
      </Paper>

      {/* Intro Section */}
      <Container maxWidth="lg" sx={{ my: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography 
              variant="h3" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              {t('about.history.title')}
            </Typography>
            
            <Typography variant="body1" paragraph>
              {t('about.history.description')}
            </Typography>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Box 
              component="img"
              src="/resources/partners/tech-partners.jpg"
              alt="Technology Partners"
              sx={{ 
                width: '100%',
                borderRadius: 4,
                boxShadow: '0 8px 40px rgba(0,0,0,0.1)'
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Partners Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom
            sx={{ 
              mb: 6,
              fontWeight: 600,
              fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            {t('about.values.title')}
          </Typography>

        <Grid container spacing={4}>
          {partners.map((partner, index) => (
            <Grid key={index} sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
              <PartnerCard
                icon={partner.icon}
                title={partner.title}
                description={partner.description}
                features={partner.features}
                website={partner.website}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
      </Box>
      
      {/* Enterprise Section */}
      <Box 
        sx={{
          py: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: 'linear-gradient(90deg, #015480, #0288d1)',
          },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
              }}
            >
              {getTranslationString('partners.enterprise.title', 'Enterprise Solutions Built with Premium Technology')}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}
            >
              {getTranslationString('partners.enterprise.description', 'We combine these powerful technologies to create custom enterprise solutions that are tailored to your specific needs and objectives. Our expertise in these platforms allows us to deliver robust and scalable applications that drive business growth.')}
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              size="large"
              component={Link}
              to="/#contact"
              sx={{ 
                px: 4, 
                py: 1.5,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                fontWeight: 500,
                letterSpacing: '0.5px',
              }}
            >
              {getTranslationString('partners.enterprise.button', 'Request Enterprise Consultation')}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Partners; 